'use client';

import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, AlertCircle } from 'lucide-react';
import { cn } from '../lib/utils';

export interface FileUploadProps {
  onFiles: (files: File[]) => void;
  accept?: string;
  maxSize?: number; // in bytes
  multiple?: boolean;
  className?: string;
  disabled?: boolean;
  label?: string;
  description?: string;
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

export function FileUpload({
  onFiles,
  accept,
  maxSize,
  multiple = false,
  className,
  disabled = false,
  label = 'Upload files',
  description,
}: FileUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);

  const validateFiles = useCallback(
    (files: File[]): { valid: File[]; error: string | null } => {
      const validFiles: File[] = [];
      for (const file of files) {
        if (maxSize && file.size > maxSize) {
          return {
            valid: [],
            error: `"${file.name}" exceeds ${formatFileSize(maxSize)} limit`,
          };
        }
        if (accept) {
          const acceptedTypes = accept.split(',').map((t) => t.trim());
          const isAccepted = acceptedTypes.some((type) => {
            if (type.startsWith('.')) {
              return file.name.toLowerCase().endsWith(type.toLowerCase());
            }
            if (type.endsWith('/*')) {
              return file.type.startsWith(type.replace('/*', '/'));
            }
            return file.type === type;
          });
          if (!isAccepted) {
            return {
              valid: [],
              error: `"${file.name}" is not an accepted file type`,
            };
          }
        }
        validFiles.push(file);
      }
      return { valid: validFiles, error: null };
    },
    [accept, maxSize],
  );

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const files = Array.from(fileList);
      const { valid, error: validationError } = validateFiles(files);
      if (validationError) {
        setError(validationError);
        return;
      }
      setError(null);
      const newFiles = multiple ? [...selectedFiles, ...valid] : valid.slice(0, 1);
      setSelectedFiles(newFiles);
      onFiles(newFiles);
    },
    [multiple, onFiles, selectedFiles, validateFiles],
  );

  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (disabled) return;
      dragCounter.current++;
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounter.current = 0;
      if (disabled) return;
      handleFiles(e.dataTransfer.files);
    },
    [disabled, handleFiles],
  );

  const removeFile = useCallback(
    (index: number) => {
      const newFiles = selectedFiles.filter((_, i) => i !== index);
      setSelectedFiles(newFiles);
      onFiles(newFiles);
      if (newFiles.length === 0 && inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [selectedFiles, onFiles],
  );

  const descriptionText =
    description ??
    [accept && `Accepted: ${accept}`, maxSize && `Max size: ${formatFileSize(maxSize)}`]
      .filter(Boolean)
      .join(' · ');

  return (
    <div className={cn('w-full', className)}>
      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && inputRef.current?.click()}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        className={cn(
          'flex cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed px-6 py-8 transition-interactive duration-200',
          isDragging
            ? 'border-statura-green-accessible bg-surface-soft'
            : 'border-slate-300 bg-white hover:border-statura-green hover:bg-surface-soft/50',
          error && 'border-red-300 bg-red-50/30',
          disabled && 'cursor-not-allowed opacity-50',
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(e) => handleFiles(e.target.files)}
          className="sr-only"
          tabIndex={-1}
        />

        <div
          className={cn(
            'mb-3 flex h-12 w-12 items-center justify-center rounded-xl',
            isDragging
              ? 'bg-statura-green-accessible/10 text-statura-green-accessible'
              : 'bg-slate-100 text-text-subtle',
          )}
        >
          <Upload className="h-6 w-6" />
        </div>

        <p className="font-body text-body-sm font-medium text-text-heading">{label}</p>
        <p className="mt-1 font-body text-caption text-text-muted">
          Drag and drop or{' '}
          <span className="font-semibold text-statura-green-accessible">browse</span>
        </p>
        {descriptionText && (
          <p className="mt-2 font-body text-caption text-text-subtle">{descriptionText}</p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </div>
      )}

      {/* File list */}
      {selectedFiles.length > 0 && (
        <ul className="mt-3 space-y-2">
          {selectedFiles.map((file, idx) => (
            <li
              key={`${file.name}-${idx}`}
              className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
            >
              <FileText className="h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0 flex-1">
                <p className="truncate font-body text-body-sm font-medium text-text-heading">
                  {file.name}
                </p>
                <p className="font-body text-caption text-text-subtle">
                  {formatFileSize(file.size)}
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile(idx);
                }}
                className="inline-flex h-6 w-6 items-center justify-center rounded text-text-subtle transition-interactive duration-200 hover:bg-slate-100 hover:text-text-body"
                aria-label={`Remove ${file.name}`}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
